import { useEffect, useState } from 'react';
import { generatePath } from 'react-router-dom';
import { ISideNavigationItem, TSideNavigationSection } from '@treezor/design-system';
import { useTranslation } from 'react-i18next';

import { useAccesControl } from '@/hooks/useAccessControl';
import { useDashboardConfig } from '@/hooks/useDashboardConfig';
import { useScreenWidth } from '@/hooks/useScreenWidth';
import { useAppDispatch, useAppSelector } from '@/hooks/redux-helpers';

import { ERole } from '@/features/roles/store/models/role.model';
import { TDashboardConfigSettingsScreen } from '@/store/models/dashboard-config.model';

import { TFeatureSlugs } from '@/features/roles/store/models/features.model';
import { setSidebar } from '@/router/store/sidebar.actions';
import { useRoutepaths } from '@/router/hooks/useRoutePaths';

interface ISidebarItem extends ISideNavigationItem {
  isDisabled: boolean;
}

type TSidebar = Omit<TSideNavigationSection, 'items' | 'label' | 'href'> & {
  items: ISidebarItem[];
};

export const useSidebar = () => {
  const { t } = useTranslation();
  const screenWidth = useScreenWidth();

  const config = useDashboardConfig();
  const { features, role: advancedRole } = useAccesControl();

  const PathRoute = useRoutepaths();

  const isDisabled = (
    screen: TDashboardConfigSettingsScreen,
    requiredRoles?: ERole[],
    requiredFeatures?: TFeatureSlugs[],
  ) => {
    const role = advancedRole.parent;
    const hasRequiredRole = !requiredRoles || (role && requiredRoles?.includes(role as ERole));
    const hasRequiredFeature =
      !requiredFeatures ||
      features.includes('ALL_APP') ||
      (features && features.some((feature) => requiredFeatures.includes(feature)));
    return !(hasRequiredRole && hasRequiredFeature && config?.settings.screens?.includes(screen));
  };

  const allSections: TSidebar[] = [
    {
      id: 'dashboard',
      icon: 'dashboard',
      items: [
        {
          id: 'dashboard-users',
          label: 'users',
          href: generatePath(PathRoute.app.root, { '*': PathRoute.app.users.main }),
          isDisabled: isDisabled('allUsers', null, ['ALL_USERS']),
        },
        {
          id: 'dashboard-kycNeedReview',
          label: 'kycNeedReview',
          href: generatePath(PathRoute.app.root, { '*': PathRoute.app.kycNeedReview }),
          isDisabled: isDisabled(
            'kycNeedReview',
            [ERole.SUPPORT_LEGAL_USER, ERole.MANAGER, ERole.ADMIN],
            ['REVIEW_DOCUMENT'],
          ),
        },
        {
          id: 'dashboard-wallets',
          label: 'wallets',
          href: generatePath(PathRoute.app.root, { '*': PathRoute.app.wallets }),
          isDisabled: isDisabled('allWallets', null, ['ALL_WALLETS']),
        },
        {
          id: 'dashboard-cards',
          label: 'cards',
          href: generatePath(PathRoute.app.root, { '*': PathRoute.app.cards }),
          isDisabled: isDisabled('allCards', null, ['ALL_CARDS']),
        },
        {
          id: 'dashboard-cardTransactions',
          label: 'cardTransactions',
          href: generatePath(PathRoute.app.root, { '*': PathRoute.app.cardTransactions }),
          isDisabled: isDisabled('allCardTransactions', null, ['ALL_CARD_TRANSACTIONS']),
        },
        {
          id: 'dashboard-beneficiaries',
          label: 'beneficiaries',
          href: generatePath(PathRoute.app.root, { '*': PathRoute.app.beneficiaries }),
          isDisabled: isDisabled('allBeneficiaries', null, ['ALL_BENEFICIARIES']),
        },
        {
          id: 'dashboard-bulkCards',
          label: 'bulkCards',
          href: generatePath(PathRoute.app.root, { '*': PathRoute.app.bulkCards.main }),
          isDisabled: isDisabled('bulkCards', [ERole.ADMIN], ['BULK_CARDS']),
        },
        {
          id: 'dashboard-onboarding',
          label: 'onboarding',
          href: generatePath(PathRoute.app.root, { '*': PathRoute.app.onboarding }),
          isDisabled: isDisabled('onboarding', null, ['USERS_ONBOARDING']),
        },
        {
          id: 'dashboard-audits',
          label: 'audits',
          href: generatePath(PathRoute.app.root, { '*': PathRoute.app.audits }),
          isDisabled: true,
        },
      ],
    },
    {
      id: 'administration',
      icon: 'settings',
      items: [
        {
          id: 'administration-dashboardUsers',
          label: 'dashboardUsers',
          href: generatePath(PathRoute.app.root, { '*': PathRoute.app.dashboardUsers }),
          isDisabled: isDisabled('supportUsers', [ERole.MANAGER, ERole.ADMIN], ['DASHBOARD_USERS']),
        },
        {
          id: 'administration-restrictions',
          label: 'restrictions',
          href: generatePath(PathRoute.app.root, { '*': PathRoute.app.restrictions.main }),
          isDisabled: isDisabled('restrictions', [ERole.ADMIN], ['PREDEF_RESTRICTIONS_GROUPS']),
        },
        {
          id: 'administration-templates',
          label: 'templates',
          href: generatePath(PathRoute.app.root, { '*': PathRoute.app.templates }),
          isDisabled: isDisabled('templates', [ERole.ADMIN], ['TEMPLATES']),
        },
        {
          id: 'administration-configuration',
          label: 'configuration',
          href: generatePath(PathRoute.app.root, { '*': PathRoute.app.configuration.main }),
          isDisabled: isDisabled('configurations', [ERole.ADMIN], ['SYSTEM_CONFIGURATION']),
        },
        {
          id: 'administration-webhooks',
          label: 'webhooks',
          href: generatePath(PathRoute.app.root, { '*': PathRoute.app.webhooks }),
          isDisabled: isDisabled('webhooks', [ERole.ADMIN], ['WEBHOOKS']),
        },
        {
          id: 'administration-encryption',
          label: 'encryption',
          href: generatePath(PathRoute.app.root, { '*': PathRoute.app.encryption }),
          isDisabled: isDisabled('encryption', [ERole.ADMIN], ['CRYPTOGRAPHY']),
        },
      ],
    },
  ];

  const shouldOpenSidebar = screenWidth >= 1280;

  const dispatch = useAppDispatch();

  const isSidebarOpen = useAppSelector((state) => state.sidebar?.isOpen);
  const [isOpen, setIsOpen] = useState(isSidebarOpen);

  const toggleSidebar = () => {
    dispatch(setSidebar(!isOpen));
    setIsOpen((isOpen) => !isOpen);
  };

  const sections: TSideNavigationSection[] = allSections.map((section) => ({
    ...section,
    label: t(`layout.sidebar.categories.${section.id}`),
    items: section.items
      .filter((item) => !item.isDisabled)
      .map((item) => {
        const { isDisabled, label, ...sidebar } = item;
        return { ...sidebar, label: t(`layout.sidebar.links.${label}`) } as ISideNavigationItem;
      }),
  }));

  const findSidebarItem = (href: string) => {
    for (const section of sections) {
      const foundItem = section.items.find((item) => item.href === href);
      if (foundItem) return foundItem;
    }
  };

  useEffect(() => {
    if (!shouldOpenSidebar) {
      setIsOpen(false);
    }
  }, [shouldOpenSidebar]);

  return { isOpen, sections, toggleSidebar, findSidebarItem };
};
